/**
 * Page Catalogue Produits E-Commerce
 * Design moderne inspiré Amazon + BackMarket
 *
 * Fonctionnalités:
 * - Hero section avec barre de recherche
 * - Filtrage par catégories (pills horizontaux)
 * - Grille produits responsive
 * - Pagination numérotée
 * - Stock en temps réel
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart, catalogApi, ArticleWithStock, ArticleCategory, enterpriseApi, EnterprisePublicInfo, getBrandInfo } from '@e-store/shared';
import { ProductCard } from '../components/ProductCard';
import { AmazonHeader } from '../components/AmazonHeader';
import './CatalogPage.css';

export function CatalogPage() {
  const [searchParams] = useSearchParams();
  const { cart, addToCart } = useCart();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState('');
  const [products, setProducts] = useState<ArticleWithStock[]>([]);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 12;

  // Filtres - initialiser depuis les query params
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');

  // Entreprise info
  const [enterprise, setEnterprise] = useState<EnterprisePublicInfo | null>(null);
  const brand = getBrandInfo();

  // Charger catégories et entreprise au montage
  useEffect(() => {
    loadCategories();
    loadEnterpriseInfo();
  }, []);

  // Synchroniser les filtres avec les query params
  useEffect(() => {
    const search = searchParams.get('search') || '';
    const category = searchParams.get('category') || '';
    if (search !== searchTerm) setSearchTerm(search);
    if (category !== selectedCategory) setSelectedCategory(category);
  }, [searchParams]);

  const loadEnterpriseInfo = async () => {
    try {
      const response = await enterpriseApi.getPublicInfo();
      if (response.data) {
        setEnterprise(response.data);
      }
    } catch (err) {
      console.error('Erreur chargement infos entreprise:', err);
    }
  };

  // Charger produits quand filtres ou page changent
  useEffect(() => {
    loadProducts();
  }, [page, selectedCategory, searchTerm]);

  const loadCategories = async () => {
    try {
      const response = await catalogApi.getCategories();
      if (response.error) {
        console.error('Erreur chargement catégories:', response.error);
        return;
      }
      setCategories(response.data || []);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await catalogApi.getProducts({
        categoryId: selectedCategory || undefined,
        search: searchTerm || undefined,
        page,
        pageSize,
      });

      if (response.error) {
        setError(response.error);
        return;
      }

      if (response.data) {
        setProducts(response.data.products || []);
        setTotal(response.data.total || 0);
        setTotalPages(Math.ceil((response.data.total || 0) / pageSize));
      }
    } catch (err: any) {
      setError('Erreur lors du chargement des produits');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (articleId: string) => {
    const article = products.find((a) => a.id === articleId);
    if (!article) return;

    const result = await addToCart({
      articleId,
      quantity: 1,
    });

    if (result.success) {
      setSuccess(`${article.name} ajouté au panier`);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Erreur lors de l\'ajout au panier');
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId === selectedCategory ? '' : categoryId);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 400, behavior: 'smooth' });
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(1);
  };

  // Générer les numéros de pages à afficher
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (page > 3) {
        pages.push('...');
      }

      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  // Handler pour la recherche depuis le header
  const handleHeaderSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  return (
    <div className="catalog-page">
      {/* Header Amazon */}
      <AmazonHeader
        showSearch={true}
        onSearch={handleHeaderSearch}
        initialSearchTerm={searchTerm}
      />

      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-background"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            Bienvenue chez <span className="highlight">{enterprise?.name || brand.name}</span>
          </h1>
          <p className="hero-subtitle">
            {enterprise?.slogan || 'Découvrez notre sélection de produits de qualité aux meilleurs prix'}
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">{total}+</span>
              <span className="stat-label">Produits</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{categories.length}</span>
              <span className="stat-label">Catégories</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">24/7</span>
              <span className="stat-label">Support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div className="catalog-container">
        {/* Messages */}
        {error && (
          <div className="message error">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        {success && (
          <div className="message success">
            <span>{success}</span>
            <button onClick={() => setSuccess('')}>×</button>
          </div>
        )}

        {/* Category Pills */}
        {categories.length > 0 && (
          <div className="categories-section">
            <h2 className="section-title">Catégories</h2>
            <div className="category-pills">
              <button
                className={`category-pill ${!selectedCategory ? 'active' : ''}`}
                onClick={() => handleCategoryClick('')}
              >
                Tous
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`category-pill ${
                    selectedCategory === category.id ? 'active' : ''
                  }`}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Résultats Info */}
        <div className="results-header">
          <div className="results-info">
            <h2 className="results-title">
              {selectedCategory
                ? categories.find(c => c.id === selectedCategory)?.name || 'Produits'
                : 'Tous les produits'}
            </h2>
            <p className="results-count">
              {total} produit{total !== 1 ? 's' : ''} trouvé{total !== 1 ? 's' : ''}
              {searchTerm && ` pour "${searchTerm}"`}
            </p>
          </div>
          {searchTerm && (
            <button
              className="btn-clear-search"
              onClick={handleClearSearch}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              Effacer
            </button>
          )}
        </div>

        {/* Grille Produits */}
        {loading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Chargement des produits...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>Aucun produit trouvé</h3>
            <p>Essayez de modifier vos critères de recherche</p>
            {searchTerm && (
              <button
                className="btn btn-primary"
                onClick={handleClearSearch}
              >
                Voir tous les produits
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="products-grid">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  article={product}
                  onAddToCart={handleAddToCart}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn pagination-arrow"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  aria-label="Page précédente"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>

                <div className="pagination-numbers">
                  {getPageNumbers().map((pageNum, idx) => (
                    pageNum === '...' ? (
                      <span key={`ellipsis-${idx}`} className="pagination-ellipsis">...</span>
                    ) : (
                      <button
                        key={pageNum}
                        className={`pagination-btn pagination-number ${page === pageNum ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum as number)}
                      >
                        {pageNum}
                      </button>
                    )
                  ))}
                </div>

                <button
                  className="pagination-btn pagination-arrow"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  aria-label="Page suivante"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>
            )}

            {/* Pagination Info */}
            <div className="pagination-info">
              Affichage {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} sur {total} produits
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="fo-footer">
        <div className="footer-content">
          <div className="footer-brand">
            {enterprise?.logoUrl ? (
              <img src={enterprise.logoUrl} alt={enterprise.name || 'Logo'} className="footer-logo" />
            ) : (
              <span className="logo-icon">{(enterprise?.name || brand.name)[0]}</span>
            )}
            <span>{enterprise?.name || brand.name}</span>
          </div>
          <p className="footer-copyright">
            © {new Date().getFullYear()} {enterprise?.name || brand.name} - Tous droits réservés
          </p>
        </div>
      </footer>
    </div>
  );
}
